/** The client's check-in — one-off or recurring — plus any intake forms to send with it.
 *
 *  A recurring choice CREATES a schedule for this client (`POST /admin/scheduled-check-in` with
 *  `traineeIds: [id]`), which is a single write with nothing to read first. What this step still
 *  will not do is add the client to an EXISTING shared schedule: that only exists as
 *  `PUT /admin/scheduled-check-in/{id}`, which replaces the whole object including its
 *  `traineeIds`, so two coaches onboarding at once would drop each other's clients. */

import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  Assignment as FormIcon,
  CameraAlt as PhotoIcon,
  MonitorWeight as WeightIcon,
  Straighten as MeasurementsIcon,
} from "@mui/icons-material";
import type { ReactNode } from "react";
import { t } from "../../../dashboard/coach/coachTokens";
import StepShell from "../StepShell";
import WizardField from "../WizardField";
import { choiceCardSx, numberFieldSx, selectSx } from "../wizardStyles";
import type { CheckInFrequency, CheckInItemType, CheckInsValues } from "../wizardReducer";

/** Same vocabulary as `ScheduledCheckInDialog`, so a schedule made here reads the same there. */
const FREQUENCY_OPTIONS: Array<{ value: CheckInFrequency; label: string }> = [
  { value: "DAILY", label: "יומי" },
  { value: "WEEKLY", label: "שבועי" },
  { value: "MONTHLY", label: "חודשי" },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: "SUNDAY", label: "א׳" },
  { value: "MONDAY", label: "ב׳" },
  { value: "TUESDAY", label: "ג׳" },
  { value: "WEDNESDAY", label: "ד׳" },
  { value: "THURSDAY", label: "ה׳" },
  { value: "FRIDAY", label: "ו׳" },
  { value: "SATURDAY", label: "ש׳" },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
  const padded = String(hour).padStart(2, "0");
  return { value: `${padded}:00`, label: `${padded}:00` };
});

/** Hebrew labels and hues match `CheckInsPage.js`'s `ITEM_TYPE_ICONS`, so a check-in requested
 *  here is recognisable on the check-ins screen. */
const ITEM_TYPES: Array<{
  type: CheckInItemType;
  label: string;
  hint: string;
  icon: ReactNode;
  color: string;
}> = [
  {
    type: "BODY_WEIGHT",
    label: "משקל",
    hint: "המתאמן מזין שקילה",
    icon: <WeightIcon sx={{ fontSize: 17 }} />,
    color: t.blue,
  },
  {
    type: "BODY_MEASUREMENT",
    label: "מדידות",
    hint: "היקפים לפי המדדים שהוגדרו",
    icon: <MeasurementsIcon sx={{ fontSize: 17 }} />,
    color: t.purple,
  },
  {
    type: "PROGRESS_PHOTO",
    label: "תמונות התקדמות",
    hint: "צילומי לפני/אחרי",
    icon: <PhotoIcon sx={{ fontSize: 17 }} />,
    color: t.green,
  },
];

interface FormTemplateOption {
  id: number;
  name?: string;
}

interface CheckInsFormsStepProps {
  values: CheckInsValues;
  error: string | null;
  formTemplates: FormTemplateOption[];
  loading: boolean;
  onChange: (values: Partial<CheckInsValues>) => void;
}

const toggle = <T,>(list: T[], item: T): T[] =>
  list.includes(item) ? list.filter(entry => entry !== item) : [...list, item];

const CheckInsFormsStep = ({
  values,
  error,
  formTemplates,
  loading,
  onChange,
}: CheckInsFormsStepProps) => (
  <StepShell
    title="צ'ק-אין וטפסים"
    description="אפשר לבקש צ'ק-אין חד-פעמי עכשיו, או להגדיר צ'ק-אין שחוזר על עצמו."
    error={error}
  >
    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: t.text, marginBottom: "8px" }}>
      מתי לבקש
    </Typography>
    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: "12px" }}>
      <Box
        component="button"
        type="button"
        aria-pressed={values.mode === "once"}
        onClick={() => onChange({ mode: "once" })}
        sx={choiceCardSx(values.mode === "once")}
      >
        <Box sx={{ fontSize: "13.5px", fontWeight: 700 }}>חד-פעמי</Box>
        <Box sx={{ fontSize: "11.5px", color: t.t2, lineHeight: 1.45 }}>
          בקשה אחת שנשלחת למתאמן מיד
        </Box>
      </Box>
      <Box
        component="button"
        type="button"
        aria-pressed={values.mode === "recurring"}
        onClick={() => onChange({ mode: "recurring" })}
        sx={choiceCardSx(values.mode === "recurring")}
      >
        <Box sx={{ fontSize: "13.5px", fontWeight: 700 }}>חוזר</Box>
        <Box sx={{ fontSize: "11.5px", color: t.t2, lineHeight: 1.45 }}>
          נשלח אוטומטית לפי תדירות שתבחר
        </Box>
      </Box>
    </Box>

    {values.mode === "recurring" && (
      <Box
        sx={{
          marginTop: "16px",
          padding: "16px",
          borderRadius: t.rs,
          backgroundColor: t.bg,
          border: `1px solid ${t.border}`,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: "16px",
          alignItems: "start",
        }}
      >
        <WizardField label="תדירות">
          <FormControl fullWidth size="small">
            <Select
              value={values.frequency}
              inputProps={{ "aria-label": "תדירות" }}
              sx={selectSx}
              onChange={event =>
                onChange({ frequency: event.target.value as CheckInFrequency })
              }
            >
              {FREQUENCY_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </WizardField>

        <WizardField label="שעת שליחה">
          <FormControl fullWidth size="small">
            <Select
              value={values.scheduleTime}
              inputProps={{ "aria-label": "שעת שליחה" }}
              sx={selectSx}
              onChange={event => onChange({ scheduleTime: String(event.target.value) })}
            >
              {HOUR_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </WizardField>

        {values.frequency === "WEEKLY" && (
          <>
            <WizardField label="באילו ימים" hint="אפשר לבחור יותר מיום אחד">
              <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {DAY_OF_WEEK_OPTIONS.map(day => {
                  const picked = values.scheduleDays.includes(day.value);
                  return (
                    <Box
                      key={day.value}
                      component="button"
                      type="button"
                      aria-pressed={picked}
                      aria-label={day.value}
                      onClick={() => onChange({ scheduleDays: toggle(values.scheduleDays, day.value) })}
                      sx={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        border: `1px solid ${picked ? t.green : t.border}`,
                        backgroundColor: picked ? t.green : t.white,
                        color: picked ? t.white : t.t2,
                      }}
                    >
                      {day.label}
                    </Box>
                  );
                })}
              </Box>
            </WizardField>

            <WizardField label="כל כמה שבועות">
              <TextField
                fullWidth
                size="small"
                type="number"
                value={values.intervalWeeks}
                inputProps={{ min: 1, max: 12, "aria-label": "כל כמה שבועות" }}
                sx={numberFieldSx}
                onChange={event =>
                  onChange({ intervalWeeks: Math.max(1, Number(event.target.value) || 1) })
                }
              />
            </WizardField>
          </>
        )}

        {values.frequency === "MONTHLY" && (
          <>
            <WizardField label="בכל חודש בתאריך">
              <TextField
                fullWidth
                size="small"
                type="number"
                value={values.daysOfMonth[0] ?? 1}
                inputProps={{ min: 1, max: 28, "aria-label": "בכל חודש בתאריך" }}
                sx={numberFieldSx}
                onChange={event =>
                  onChange({
                    daysOfMonth: [Math.min(28, Math.max(1, Number(event.target.value) || 1))],
                  })
                }
              />
            </WizardField>

            <WizardField label="כל כמה חודשים">
              <TextField
                fullWidth
                size="small"
                type="number"
                value={values.intervalMonths}
                inputProps={{ min: 1, max: 12, "aria-label": "כל כמה חודשים" }}
                sx={numberFieldSx}
                onChange={event =>
                  onChange({ intervalMonths: Math.max(1, Number(event.target.value) || 1) })
                }
              />
            </WizardField>
          </>
        )}
      </Box>
    )}

    <Typography
      sx={{ fontSize: "14px", fontWeight: 700, color: t.text, marginTop: "24px", marginBottom: "8px" }}
    >
      מה לבקש מהמתאמן
    </Typography>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "4px 20px" }}>
      {ITEM_TYPES.map(item => (
        <FormControlLabel
          key={item.type}
          control={
            <Checkbox
              size="small"
              checked={values.itemTypes.includes(item.type)}
              onChange={() => onChange({ itemTypes: toggle(values.itemTypes, item.type) })}
              sx={{ color: t.t3, "&.Mui-checked": { color: t.green } }}
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box sx={{ color: item.color, display: "flex" }}>{item.icon}</Box>
              <Box>
                <Box sx={{ fontSize: "13px", color: t.text }}>{item.label}</Box>
                <Box sx={{ fontSize: "11px", color: t.t3 }}>{item.hint}</Box>
              </Box>
            </Box>
          }
        />
      ))}
    </Box>

    <Box sx={{ marginTop: "24px", paddingTop: "18px", borderTop: `1px solid ${t.borderLight}` }}>
      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: t.text }}>
        טפסים לשליחה
      </Typography>
      <Typography sx={{ fontSize: "12px", color: t.t3, marginTop: "2px", marginBottom: "10px" }}>
        למשל הצהרת בריאות או שאלון פתיחה.
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: t.t2, fontSize: "13px" }}>
          <CircularProgress size={16} sx={{ color: t.green }} />
          טוען טפסים…
        </Box>
      ) : formTemplates.length === 0 ? (
        <Box sx={{ fontSize: "13px", color: t.t2 }}>אין טפסים מוגדרים בחברה.</Box>
      ) : (
        <Box
          sx={{
            maxHeight: "200px",
            overflowY: "auto",
            border: `1px solid ${t.border}`,
            borderRadius: t.rs,
            padding: "6px 10px",
          }}
        >
          {formTemplates.map(template => (
            <FormControlLabel
              key={template.id}
              sx={{ display: "flex", width: "100%" }}
              control={
                <Checkbox
                  size="small"
                  checked={values.formTemplateIds.includes(template.id)}
                  onChange={() =>
                    onChange({ formTemplateIds: toggle(values.formTemplateIds, template.id) })
                  }
                  sx={{ color: t.t3, "&.Mui-checked": { color: t.green } }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <FormIcon sx={{ fontSize: 16, color: t.amber }} />
                  <Box sx={{ fontSize: "13px", color: t.text }}>{template.name}</Box>
                </Box>
              }
            />
          ))}
        </Box>
      )}
    </Box>
  </StepShell>
);

export default CheckInsFormsStep;
